import { apiRequest } from '../../utilities/apiHelper';

/**
 * Convert a "YYYY-MM" month string to an ISO datetime string at noon UTC.
 * Using noon UTC ensures the date is unambiguous across all timezones
 * (no risk of crossing month boundaries when the backend does timezone conversion).
 */
function monthToIsoDay(yyyyMm, isEndOfMonth = false) {
  const [year, month] = yyyyMm.split('-').map(Number);
  if (isEndOfMonth) {
    // Last day of the month at noon UTC
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return `${yyyyMm}-${String(lastDay).padStart(2, '0')}T12:00:00.000Z`;
  }
  return `${yyyyMm}-01T12:00:00.000Z`;
}

/**
 * Map the backend report shape to what the frontend expects.
 * Normalises field names that differ between API response and UI code.
 */
function normaliseReport(report) {
  if (!report || !report.sections) return report;
  return {
    ...report,
    sections: report.sections.map((section) => ({
      ...section,
      name: section.sectionName ?? section.name,
    })),
  };
}

export async function fetchNarrativeReportsApi(orgId, filters = {}) {
  const params = new URLSearchParams();
  if (filters.framework && filters.framework !== 'all') params.set('framework', filters.framework);
  if (filters.status && filters.status !== 'all') params.set('status', filters.status);
  if (filters.startDate) params.set('start_date', filters.startDate);
  if (filters.endDate) params.set('end_date', filters.endDate);
  if (filters.page) params.set('page', filters.page);
  if (filters.limit) params.set('limit', filters.limit);

  const query = params.toString();
  const url = `${orgId}/narrative-reports${query ? `?${query}` : ''}`;
  return apiRequest('GET', url, null, true);
}

export async function createNarrativeReportApi(orgId, data, usrTimezone) {
  const url = `${orgId}/narrative-reports`;
  const body = {
    name: data.name,
    framework: data.framework,
    startDate: data.startDate.split('-').length === 3
      ? `${data.startDate}T12:00:00.000Z`
      : monthToIsoDay(data.startDate, false),
    endDate: data.endDate.split('-').length === 3
      ? `${data.endDate}T12:00:00.000Z`
      : monthToIsoDay(data.endDate, true),
    usrTimezone: usrTimezone,
    reportingPeriod: data.reportingPeriod ?? null,
    ...(data.sourceReportId && { sourceReportId: data.sourceReportId }),
  };
  const result = await apiRequest('POST', url, body, true);
  return normaliseReport(result);
}

export async function getNarrativeReportApi(orgId, id, auditVersion = null) {
  const query = auditVersion ? `?auditVersion=${encodeURIComponent(auditVersion)}` : '';
  const url = `${orgId}/narrative-reports/${id}${query}`;
  const result = await apiRequest('GET', url, null, true);
  return normaliseReport(result);
}

export async function saveNarrativeReportApi(orgId, id, sections) {
  const url = `${orgId}/narrative-reports/${id}`;
  const body = {
    sections: sections.map((section) => ({
      sectionKey: section.sectionKey,
      completed: !!section.completed,
      editableFields: (section.editableFields || []).map((f) => ({
        fieldKey: f.fieldKey,
        value: f.value ?? null,
      })),
    })),
  };
  const result = await apiRequest('PUT', url, body, false);
  return normaliseReport(result);
}

/** POST /preview — backend renders HTML from submitted values, no DB writes */
export async function previewNarrativeReportApi(orgId, id, sections) {
  const url = `${orgId}/narrative-reports/${id}/preview`;
  const body = {
    sections: sections.map((section) => ({
      sectionKey: section.sectionKey,
      editableFields: (section.editableFields || []).map((f) => ({
        fieldKey: f.fieldKey,
        value: f.value ?? null,
      })),
    })),
  };
  return apiRequest('POST', url, body, true);
}

/** POST /finalize — saves fields + generates narrative + locks report (status → completed) */
export async function finalizeNarrativeReportApi(orgId, id, sections) {
  const url = `${orgId}/narrative-reports/${id}/finalize`;
  const body = {
    sections: sections.map((section) => ({
      sectionKey: section.sectionKey,
      completed: !!section.completed,
      editableFields: (section.editableFields || []).map((f) => ({
        fieldKey: f.fieldKey,
        value: f.value ?? null,
      })),
    })),
  };
  const result = await apiRequest('POST', url, body, true);
  return normaliseReport(result);
}

export async function deleteNarrativeReportApi(orgId, id) {
  const url = `${orgId}/narrative-reports/${id}`;
  return apiRequest('DELETE', url, null, true);
}

export async function getAuditHistoryApi(orgId, id) {
  const url = `${orgId}/narrative-reports/${id}/audit-history`;
  return apiRequest('GET', url, null, true);
}

export async function restoreAuditVersionApi(orgId, id, version) {
  const url = `${orgId}/narrative-reports/${id}/audit-history/${encodeURIComponent(version)}/restore`;
  const result = await apiRequest('POST', url, null, true);
  return normaliseReport(result);
}

export async function getDataSetApi(orgId, id) {
  const url = `${orgId}/narrative-reports/${id}/dataset`;
  return apiRequest('GET', url, null, true);
}
